import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Flex,
  Text,
  VStack,
  HStack,
  Heading,
  Grid,
  GridItem,
  Card,
  CardBody,
  IconButton,
  Button,
  Badge,
  useColorMode,
  useColorModeValue,
  Icon,
  Spinner,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  useToast,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  CardHeader,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  useDisclosure,
  FormControl,
  FormLabel,
  Switch,
  Radio,
  RadioGroup,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
} from '@chakra-ui/react';
import {
  RepeatIcon,
  CheckCircleIcon,
  WarningIcon,
  EmailIcon,
  SearchIcon,
  CalendarIcon,
  AddIcon,
  EditIcon,
  DeleteIcon
} from '@chakra-ui/icons';
import { useSearchParams } from 'react-router-dom';
import apiService from '../services/apiService';
import { MdPeople } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { ToastManager } from '../components/Common/ToastManager';
/**
 * Required API Service Methods:
 * - apiService.fetchClientProfileDetails(clientId)
 * - apiService.fetchClientApplicationMappings({ clientId })
 * - apiService.fetchSupportPersons(clientId)
 * - apiService.addSupportPerson(clientId, personData)
 * - apiService.updateSupportPerson(clientId, personId, personData)
 * - apiService.deleteSupportPerson(clientId, personId)
 */

// Custom Icons
const OnlineIcon = (props) => (
  <Icon viewBox="0 0 16 16" {...props}>
    <circle cx="8" cy="8" r="6" fill="currentColor" />
  </Icon>
);

const VersionIcon = (props) => (
  <Icon viewBox="0 0 24 24" {...props}>
    <path fill="currentColor" d="M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M12,20C7.59,20 4,16.41 4,12C4,7.59 7.59,4 12,4C16.41,4 20,7.59 20,12C20,16.41 16.41,20 12,20M15,12L13,10V7H11V11L13.5,13.5L15,12Z"/>
  </Icon>
);

const IssuesIcon = (props) => (
  <Icon viewBox="0 0 24 24" {...props}>
    <path fill="currentColor" d="M11,15H13V17H11V15M11,7H13V13H11V7M12,2C6.47,2 2,6.5 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M12,20A8,8 0 0,1 4,12A8,8 0 0,1 12,4A8,8 0 0,1 20,12A8,8 0 0,1 12,20Z"/>
  </Icon>
);

const ApplicationsIcon = (props) => (
  <Icon viewBox="0 0 24 24" {...props}>
    <path fill="currentColor" d="M19,3H5C3.89,3 3,3.89 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V5C21,3.89 20.1,3 19,3M19,5V19H5V5H19M7,7V9H9V7H7M11,7V9H13V7H11M15,7V9H17V7H15M7,11V13H9V11H7M11,11V13H13V11H11M15,11V13H17V11H15M7,15V17H9V15H7M11,15V17H13V15H11M15,15V17H17V15H15Z"/>
  </Icon>
);

function ClientProfilePage({ userProfile, appConfig, authServiceReady }) {
  const { colorMode } = useColorMode();
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const [searchParams] = useSearchParams();
  const cancelRef = React.useRef();
  
  // ALL COLOR MODE VALUES MUST BE DECLARED AT THE TOP - BEFORE ANY EARLY RETURNS
  const bg = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');
  const textColor = useColorModeValue('gray.900', 'white');
  const mutedColor = useColorModeValue('gray.600', 'gray.400');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const modalHeaderBg = useColorModeValue('blue.50', 'gray.900');
  const modalFooterBg = useColorModeValue('gray.50', 'gray.900');
  const formBg = useColorModeValue('gradient.100', 'gray.700');
  const emptyStateBg = useColorModeValue('gray.50', 'gray.700');
  const emptyStateIconBg = useColorModeValue('white', 'gray.800');
  const teamBoxBg = useColorModeValue('blue.50', 'blue.900');
  const editButtonHoverBg = useColorModeValue('blue.50', 'blue.900');
  const deleteButtonHoverBg = useColorModeValue('red.50', 'red.900');
  
  // State
  const [loading, setLoading] = useState(true);
  const [clientData, setClientData] = useState(null);
  const [applications, setApplications] = useState([]);
  const [filteredApplications, setFilteredApplications] = useState([]);
  const [supportPersons, setSupportPersons] = useState([]);
  const [loadingSupportPersons, setLoadingSupportPersons] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  
  // Add Support Person Form State
  const [newPerson, setNewPerson] = useState({
    name: '',
    email: '',
    isLead: 0,
    team: 'PS team'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingPerson, setEditingPerson] = useState(null);
  const [deletingPerson, setDeletingPerson] = useState(null);
  
  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [paginatedApps, setPaginatedApps] = useState([]);
  const navigate = useNavigate();

  // Get clientId from URL params
  useEffect(() => {
    const clientId = searchParams.get('clientId');
    if (clientId) {
      fetchClientData(clientId);
    } else {
      setLoading(false);
      toast({
        title: 'Missing Client ID',
        description: 'Client ID is required in URL parameters',
        status: 'warning',
        duration: 4000,
        isClosable: true,
      });
    }
  }, [searchParams]);

  const fetchClientData = async (clientId) => {
    try {
      setLoading(true);
      
      // Fetch client profile details
      const profileResponse = await apiService.fetchClientProfileDetails(clientId);
      
      if (profileResponse.success && profileResponse.data.length > 0) {
        const profile = profileResponse.data[0];
        
        // Fetch installed applications
        const appsResponse = await apiService.fetchClientApplicationMappings({ clientId });
        
        const apps = appsResponse.success ? appsResponse.data : [];
        
        setClientData({
          id: profile.clientId,
          name: profile.clientName,
          status: profile.clientStatus.toUpperCase(),
          lastOnline: new Date(profile.lastHeartbeat).toLocaleString(),
          currentVersion: `${profile.prismVersion}`,
          openIssuesCount: profile.issuesOpen,
          reportPerson: profile.reportPerson,
          totalApplicationsCount: profile.applicationsInstalled,
          eventsScheduledCount: profile.eventsScheduled,
          supportPersons: profile.supportPersons,
        });
        
        setApplications(apps);
        setFilteredApplications(apps);
      } else {
        setClientData(null);
        toast({
          title: 'Client not found',
          description: 'No client data found for the provided ID',
          status: 'error',
          duration: 4000,
          isClosable: true,
        });
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching client data:', error);
      toast({
        title: 'Error loading client data',
        description: error.message || 'Failed to fetch client information',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
      setLoading(false);
    }
  };

  // Filter applications
  useEffect(() => {
    let filtered = [...applications];
    
    if (searchQuery) {
      filtered = filtered.filter(app =>
        app.appName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        app.appDescription.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    if (statusFilter !== 'all') {
      const isEnabled = statusFilter === 'enabled';
      filtered = filtered.filter(app => app.isEnabled === isEnabled);
    }
    
    if (typeFilter !== 'all') {
      filtered = filtered.filter(app => app.appType === typeFilter);
    }
    
    setFilteredApplications(filtered);
    setCurrentPage(1);
  }, [searchQuery, statusFilter, typeFilter, applications]);

  // Paginate
  useEffect(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    setPaginatedApps(filteredApplications.slice(startIndex, endIndex));
  }, [filteredApplications, currentPage, pageSize]);

  const totalPages = Math.ceil(filteredApplications.length / pageSize);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleRefresh = () => {
    const clientId = searchParams.get('clientId');
    if (clientId) {
      toast({
        title: 'Refreshing client data...',
        status: 'info',
        duration: 2000,
        isClosable: true,
      });
      fetchClientData(clientId);
    }
  };

  const clearFilters = () => {
    setSearchQuery('');
    setStatusFilter('all');
    setTypeFilter('all');
    setCurrentPage(1);
  };

  const fetchSupportPersons = async (clientId) => {
    try {
      setLoadingSupportPersons(true);
      const response = await apiService.fetchSupportPersons(clientId);
      
      if (response.success) {
        setSupportPersons(response.data);
      } else {
        setSupportPersons([]);
      }
      setLoadingSupportPersons(false);
    } catch (error) {
      console.error('Error fetching support persons:', error);
      toast({
        title: 'Error loading support persons',
        description: error.message || 'Failed to fetch support team',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
      setLoadingSupportPersons(false);
    }
  };

  const handleViewDetails = () => {
    const clientId = searchParams.get('clientId');
    if (clientId) {
      fetchSupportPersons(clientId);
      setShowAddForm(false);
      onOpen();
    }
  };

  const handleToggleAddForm = () => {
    setShowAddForm(!showAddForm);
    if (showAddForm) {
      setNewPerson({
        name: '',
        email: '',
        isLead: 0,
        team: 'PS team'
      });
      setEditingPerson(null);
    }
  };

  const handleEditPerson = (person) => {
    setNewPerson({
      name: person.name,
      email: person.email,
      isLead: person.isLead,
      team: person.team
    });
    setEditingPerson(person);
    setShowAddForm(true);
  };

  const handleDeletePerson = (person) => {
    setDeletingPerson(person);
    onDeleteOpen();
  };

  const confirmDeletePerson = async () => {
    if (!deletingPerson) return;

    try {
      const clientId = searchParams.get('clientId');
      const response = await apiService.deleteSupportPerson(clientId, deletingPerson.id);
      
      if (response.success) {
        toast({
          title: 'Success',
          description: 'Support person deleted successfully',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        
        fetchSupportPersons(clientId);
      } else {
        toast({
          title: 'Error',
          description: response.message || 'Failed to delete support person',
          status: 'error',
          duration: 4000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error deleting support person:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete support person',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setDeletingPerson(null);
      onDeleteClose();
    }
  };

  const handleAddPerson = async () => {
    if (!newPerson.name.trim() || !newPerson.email.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Please fill in all required fields',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newPerson.email)) {
      toast({
        title: 'Invalid Email',
        description: 'Please enter a valid email address',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      setIsSubmitting(true);
      const clientId = searchParams.get('clientId');
      
      let response;
      if (editingPerson) {
        response = await apiService.updateSupportPerson(clientId, editingPerson.id, newPerson);
      } else {
        response = await apiService.addSupportPerson(clientId, newPerson);
      }
      
      if (response.success) {
        toast({
          title: 'Success',
          description: editingPerson ? 'Support person updated successfully' : 'Support person added successfully',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        
        setNewPerson({
          name: '',
          email: '',
          isLead: 0,
          team: 'PS team'
        });
        setEditingPerson(null);
        
        fetchSupportPersons(clientId);
        setShowAddForm(false);
      } else {
        toast({
          title: 'Error',
          description: response.message || `Failed to ${editingPerson ? 'update' : 'add'} support person`,
          status: 'error',
          duration: 4000,
          isClosable: true,
        });
      }
      
      setIsSubmitting(false);
    } catch (error) {
      console.error('Error saving support person:', error);
      toast({
        title: 'Error',
        description: error.message || `Failed to ${editingPerson ? 'update' : 'add'} support person`,
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
      setIsSubmitting(false);
    }
  };


  const handleViewClientIssues = useCallback((clientId, clientName, tabIndex = 0) => {
    try {
        const params = new URLSearchParams();
        params.set('clientId', clientId.toString());
        params.set('clientName', clientName);
        params.set('tab', tabIndex.toString());

        const issuesPath = `/issues?${params.toString()}`;
        navigate(issuesPath);
    } catch (error) {
        console.error('Failed to navigate to client issues:', error);
        ToastManager.showError('Failed to open client issues');
    }
}, [navigate]);

 const handleViewClientMappings = useCallback((applicationName) => {
    try {
      const mappingsPath = `/client-appmappings?filterByApplication=${encodeURIComponent(applicationName)}`;
      navigate(mappingsPath);
    } catch (error) {
      console.error('Failed to navigate to client mappings:', error);
      // Add toast error handling if you have toast
    }
  }, [navigate]);

  const handleViewClientEvents = useCallback((clientId)=>{
    try{
      const eventsPath=`/event-scheduling?clientId=${clientId}`;
      navigate(eventsPath);
    }catch(error){
      console.error("Failed to navigate Client Events")
    }
  },[navigate])

  const appTypes = ['all', ...new Set(applications.map(app => app.appType))];

  if (loading) {
    return (
      <Flex minH="100vh" bg={bg} align="center" justify="center">
        <VStack spacing={4}>
          <Spinner size="xl" color="blue.500" thickness="4px" />
          <Text color={mutedColor}>Loading client profile...</Text>
        </VStack>
      </Flex>
    );
  }

  if (!clientData) {
    return (
      <Flex minH="100vh" bg={bg} align="center" justify="center">
        <VStack spacing={4}>
          <WarningIcon boxSize={12} color="red.500" />
          <Text color={mutedColor}>Client not found</Text>
          <Button onClick={() => window.history.back()}>Go Back</Button>
        </VStack>
      </Flex>
    );
  }

  return (
    <Box minH="100vh" bg={bg} p={8}>
      {/* Header */}
      <Flex justify="flex-end" align="center" mb={6}>
        <Button
          leftIcon={<RepeatIcon />}
          size="sm"
          variant="outline"
          onClick={handleRefresh}
          borderColor={borderColor}
        >
          Refresh
        </Button>
      </Flex>

      {/* Client Name and Status */}
      <VStack align="start" spacing={2} mb={8}>
        <HStack spacing={3}>
          <Heading size="xl" color={textColor}>{clientData.name}</Heading>
          <Badge
            colorScheme={clientData.status === 'ONLINE' ? 'green' : 'red'}
            fontSize="sm"
            px={3}
            py={1}
            borderRadius="full"
          >
            <HStack spacing={1}>
              <OnlineIcon boxSize={2} />
              <Text>{clientData.status}</Text>
            </HStack>
          </Badge>
        </HStack>
        <Text color={mutedColor} fontSize="sm">
          Last online: {clientData.lastOnline}
        </Text>
      </VStack>

      {/* Stats Cards */}
      <Grid templateColumns="repeat(4, 1fr)" gap={6} mb={8}>
        <GridItem>
          <Card bg={cardBg} shadow="sm" borderRadius="xl" border="1px solid" borderColor={borderColor}>
            <CardBody>
              <VStack align="start" spacing={2}>
                <HStack color={mutedColor}>
                  <VersionIcon boxSize={5} />
                  <Text fontSize="sm" fontWeight="medium">Current Version</Text>
                </HStack>
                <Text fontSize="2xl" fontWeight="bold" color={textColor}>
                  {clientData.currentVersion}
                </Text>
              </VStack>
            </CardBody>
          </Card>
        </GridItem>

        <GridItem>
          <Card bg={cardBg} shadow="sm" borderRadius="xl" border="1px solid" borderColor={borderColor} cursor="pointer">
            <CardBody onClick={() => handleViewClientIssues(clientData.id, clientData.name, 0)}>
              <VStack align="start" spacing={2}>
                <HStack color={mutedColor}>
                  <IssuesIcon boxSize={5} />
                  <Text fontSize="sm" fontWeight="medium">Issues Open</Text>
                </HStack>
                <Text fontSize="2xl" fontWeight="bold" color="red.500">
                  {clientData.openIssuesCount}
                </Text>
              </VStack>
            </CardBody>
          </Card>
        </GridItem>

        <GridItem>
          <Card bg={cardBg} shadow="sm" borderRadius="xl" border="1px solid" borderColor={borderColor}>
            <CardHeader pb={2}>
              <HStack justify="space-between" align="center">
                <HStack color={mutedColor}>
                  <EmailIcon boxSize={5} />
                  <Text fontSize="sm" fontWeight="medium">Report Person</Text>
                </HStack>
                {/* <Button size="xs" variant="ghost" colorScheme="blue" >
                  View Details
                </Button> */}
              </HStack>
            </CardHeader>
            <CardBody pt={2}>
              <VStack align="start" spacing={2}>
                <Text fontSize="sm" color={textColor} noOfLines={2}>
                  {clientData.reportPerson}
                </Text>
              </VStack>
            </CardBody>
          </Card>
        </GridItem>

        <GridItem>
          <Card bg={cardBg} shadow="sm" borderRadius="xl" border="1px solid" borderColor={borderColor} cursor="pointer">
            <CardBody onClick={handleViewDetails}>
              <VStack align="start" spacing={2}>
                <HStack color={mutedColor}>
                  <MdPeople boxSize={5} />
                  <Text fontSize="sm" fontWeight="medium">Support Team Members</Text>
                </HStack>
                <Text fontSize="2xl" fontWeight="bold" color={textColor}>
                  {clientData.supportPersons}
                </Text>
              </VStack>
            </CardBody>
          </Card>
        </GridItem>
        <GridItem>
          <Card bg={cardBg} shadow="sm" borderRadius="xl" border="1px solid" borderColor={borderColor} cursor="pointer">
            <CardBody>
              <VStack align="start" spacing={2}>
                <HStack color={mutedColor}>
                  <ApplicationsIcon boxSize={5} />
                  <Text fontSize="sm" fontWeight="medium">Applications Installed</Text>
                </HStack>
                <Text fontSize="2xl" fontWeight="bold" color={textColor}>
                  {clientData.totalApplicationsCount}
                </Text>
              </VStack>
            </CardBody>
          </Card>
        </GridItem>

        <GridItem>
          <Card bg={cardBg} shadow="sm" borderRadius="xl" border="1px solid" borderColor={borderColor} cursor="pointer">
            <CardBody onClick={()=>handleViewClientEvents(clientData.id)}>
              <VStack align="start" spacing={2}>
                <HStack color={mutedColor}>
                  <CalendarIcon boxSize={5} />
                  <Text fontSize="sm" fontWeight="medium">Events scheduled</Text>
                </HStack>
                <Text fontSize="2xl" fontWeight="bold" color={textColor}>
                  {clientData.eventsScheduledCount}
                </Text>
              </VStack>
            </CardBody>
          </Card>
        </GridItem>
      </Grid>

      {/* Applications Table */}
      <Card bg={cardBg} shadow="sm" borderRadius="xl" border="1px solid" borderColor={borderColor}>
        <CardBody>
          <VStack align="stretch" spacing={4}>
            <Flex justify="space-between" align="center">
              <Heading size="md" color={textColor}>Installed Applications</Heading>
              <HStack spacing={4}>
                <Text fontSize="sm" color={mutedColor}>
                  Showing {paginatedApps.length > 0 ? (currentPage - 1) * pageSize + 1 : 0} - {Math.min(currentPage * pageSize, filteredApplications.length)} of {filteredApplications.length} applications
                </Text>
                <Select
                  size="sm"
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  maxW="100px"
                  bg={bg}
                  borderColor={borderColor}
                >
                  <option value={5}>5 per page</option>
                  <option value={10}>10 per page</option>
                  <option value={25}>25 per page</option>
                  <option value={50}>50 per page</option>
                </Select>
              </HStack>
            </Flex>

            <Flex gap={3} flexWrap="wrap">
              <InputGroup maxW="300px">
                <InputLeftElement pointerEvents="none">
                  <SearchIcon color={mutedColor} />
                </InputLeftElement>
                <Input
                  placeholder="Search applications..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  bg={bg}
                  borderColor={borderColor}
                  _hover={{ borderColor: 'gray.300' }}
                />
              </InputGroup>

              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                maxW="180px"
                bg={bg}
                borderColor={borderColor}
                _hover={{ borderColor: 'gray.300' }}
              >
                <option value="all">All Status</option>
                <option value="enabled">Enabled</option>
                <option value="disabled">Disabled</option>
              </Select>

              <Select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                maxW="200px"
                bg={bg}
                borderColor={borderColor}
                _hover={{ borderColor: 'gray.300' }}
              >
                {appTypes.map(type => (
                  <option key={type} value={type}>
                    {type === 'all' ? 'All Types' : type}
                  </option>
                ))}
              </Select>

              {(searchQuery || statusFilter !== 'all' || typeFilter !== 'all') && (
                <Button size="sm" variant="ghost" onClick={clearFilters} colorScheme="blue">
                  Clear Filters
                </Button>
              )}
            </Flex>

            <TableContainer>
              <Table variant="simple" size="md">
                <Thead>
                  <Tr>
                    <Th color={mutedColor}>Application Name</Th>
                    <Th color={mutedColor}>Type</Th>
                    <Th color={mutedColor}>App Version</Th>
                    <Th color={mutedColor}>Mapping Version</Th>
                    <Th color={mutedColor}>Min Version</Th>
                    <Th color={mutedColor}>Status</Th>
                    <Th color={mutedColor}>Description</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {paginatedApps.length > 0 ? (
                    paginatedApps.map((app) => (
                      <Tr key={app.appId} _hover={{ bg: hoverBg }} transition="background 0.2s">
                        <Td py={4} borderColor={borderColor}>
                                                    <VStack align="start" spacing={1}>
                                                      <Text
                                                        fontWeight="medium"
                                                        fontSize="sm"
                                                        color="blue.500"
                                                        cursor="pointer"
                                                        _hover={{ textDecoration: 'underline', color: 'blue.600' }}
                                                        onClick={() => handleViewClientMappings(app.appName)}
                                                      >
                                                        {app.appName}
                                                      </Text>
                                                      <Text fontSize="xs">
                                                        ID: {app.appId}
                                                      </Text>
                                                    </VStack>
                                                  </Td>
                        <Td>
                          <Badge colorScheme="purple" variant="subtle">{app.appType}</Badge>
                        </Td>
                        <Td color={textColor}>{app.appVersion}</Td>
                        <Td color={textColor}>{app.mappingVersion}</Td>
                        <Td color={mutedColor}>{app.minimalVersion}</Td>
                        <Td>
                          <Badge colorScheme={app.isEnabled ? 'green' : 'gray'} variant="subtle">
                            <HStack spacing={1}>
                              <Icon as={app.isEnabled ? CheckCircleIcon : WarningIcon} boxSize={3} />
                              <Text>{app.isEnabled ? 'Enabled' : 'Disabled'}</Text>
                            </HStack>
                          </Badge>
                        </Td>
                        <Td color={mutedColor} fontSize="sm" maxW="250px" whiteSpace="normal" wordBreak="break-word">
                          {app.appDescription}
                        </Td>
                      </Tr>
                    ))
                  ) : (
                    <Tr>
                      <Td colSpan={7} textAlign="center" py={8}>
                        <VStack spacing={2}>
                          <WarningIcon boxSize={8} color={mutedColor} />
                          <Text color={mutedColor}>No applications found</Text>
                          {(searchQuery || statusFilter !== 'all' || typeFilter !== 'all') && (
                            <Button size="sm" variant="link" colorScheme="blue" onClick={clearFilters}>
                              Clear filters to see all applications
                            </Button>
                          )}
                        </VStack>
                      </Td>
                    </Tr>
                  )}
                </Tbody>
              </Table>
            </TableContainer>

            {filteredApplications.length > 0 && (
              <Flex justify="space-between" align="center" mt={4} pt={4} borderTop="1px solid" borderColor={borderColor}>
                <Text fontSize="sm" color={mutedColor}>
                  Page {currentPage} of {totalPages}
                </Text>
                <HStack spacing={2}>
                  <Button size="sm" onClick={() => handlePageChange(1)} isDisabled={currentPage === 1} variant="outline" borderColor={borderColor}>
                    First
                  </Button>
                  <Button size="sm" onClick={() => handlePageChange(currentPage - 1)} isDisabled={currentPage === 1} variant="outline" borderColor={borderColor}>
                    Previous
                  </Button>
                  
                  <HStack spacing={1}>
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      
                      return (
                        <Button
                          key={pageNum}
                          size="sm"
                          onClick={() => handlePageChange(pageNum)}
                          colorScheme={currentPage === pageNum ? 'blue' : 'gray'}
                          variant={currentPage === pageNum ? 'solid' : 'outline'}
                          borderColor={borderColor}
                        >
                          {pageNum}
                        </Button>
                      );
                    })}
                  </HStack>

                  <Button size="sm" onClick={() => handlePageChange(currentPage + 1)} isDisabled={currentPage === totalPages} variant="outline" borderColor={borderColor}>
                    Next
                  </Button>
                  <Button size="sm" onClick={() => handlePageChange(totalPages)} isDisabled={currentPage === totalPages} variant="outline" borderColor={borderColor}>
                    Last
                  </Button>
                </HStack>
              </Flex>
            )}
          </VStack>
        </CardBody>
      </Card>

      {/* Support Persons Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="6xl">
        <ModalOverlay backdropFilter="blur(8px)" bg="blackAlpha.700" />
        <ModalContent bg={cardBg} borderRadius="3xl" shadow="dark-lg" border="1px solid" borderColor={borderColor} maxH="90vh">
          <ModalHeader borderBottom="1px solid" borderColor={borderColor} px={8} py={6} bg={modalHeaderBg} borderTopRadius="3xl">
            <Flex justify="space-between" align="center">
              <VStack align="start" spacing={2}>
                <HStack spacing={3}>
                  <Box p={2} bg="blue.500" borderRadius="lg" color="white">
                    <EmailIcon boxSize={6} />
                  </Box>
                  <Heading size="lg" color={textColor}>Support Team</Heading>
                </HStack>
                <Text fontSize="md" fontWeight="normal" color={mutedColor}>
                  Manage support persons for this client
                </Text>
              </VStack>
              {userProfile?.role === 'admin' && !showAddForm && (
                <Button
                  leftIcon={<AddIcon />}
                  colorScheme="blue"
                  size="lg"
                  borderRadius="xl"
                  onClick={handleToggleAddForm}
                  shadow="md"
                  px={8}
                  _hover={{ transform: 'translateY(-2px)', shadow: 'lg' }}
                  transition="all 0.2s"
                >
                  Add Person
                </Button>
              )}
            </Flex>
          </ModalHeader> 
          <ModalBody px={8} py={6} overflowY="auto">
            <VStack spacing={6} align="stretch">
              {showAddForm && (
                <Box p={6} bg={formBg} borderRadius="2xl" border="2px solid" borderColor="blue.500" shadow="lg" position="relative" overflow="hidden">
                  <Box position="absolute" top="-50%" right="-10%" width="300px" height="300px" bg="blue.500" opacity="0.05" borderRadius="full" filter="blur(40px)" />
                  
                  <VStack spacing={5} align="stretch" position="relative">
                    <Flex justify="space-between" align="center">
                      <HStack spacing={3}>
                        <Box p={2} bg="blue.500" borderRadius="lg" color="white">
                          <AddIcon />
                        </Box>
                        <VStack align="start" spacing={0}>
                          <Heading size="md" color={textColor}>
                            {editingPerson ? 'Edit Support Person' : 'Add New Support Person'}
                          </Heading>
                          <Text fontSize="sm" color={mutedColor}>
                            {editingPerson ? 'Update the details below' : 'Fill in the details below'}
                          </Text>
                        </VStack>
                      </HStack>
                      <IconButton icon={<RepeatIcon />} variant="ghost" colorScheme="gray" onClick={handleToggleAddForm} aria-label="Close form" size="lg" />
                    </Flex>

                    <Grid templateColumns="repeat(2, 1fr)" gap={5}>
                      <GridItem>
                        <FormControl isRequired>
                          <FormLabel fontSize="sm" fontWeight="bold" color={textColor}>Full Name</FormLabel>
                          <Input
                            placeholder="Enter full name"
                            value={newPerson.name}
                            onChange={(e) => setNewPerson({ ...newPerson, name: e.target.value })}
                            size="lg"
                            borderRadius="xl"
                            bg={cardBg}
                            borderColor={borderColor}
                            borderWidth="2px"
                            _hover={{ borderColor: 'blue.300' }}
                            _focus={{ borderColor: 'blue.500', boxShadow: '0 0 0 3px rgba(66, 153, 225, 0.15)' }}
                          />
                        </FormControl>
                      </GridItem>

                      <GridItem>
                        <FormControl isRequired>
                          <FormLabel fontSize="sm" fontWeight="bold" color={textColor}>Email Address</FormLabel>
                          <InputGroup size="lg">
                            <InputLeftElement pointerEvents="none" height="full">
                              <EmailIcon color="blue.500" boxSize={5} />
                            </InputLeftElement>
                            <Input
                              type="email"
                              placeholder="name@company.com"
                              value={newPerson.email}
                              onChange={(e) => setNewPerson({ ...newPerson, email: e.target.value })}
                              borderRadius="xl"
                              bg={cardBg}
                              borderColor={borderColor}
                              borderWidth="2px"
                              _hover={{ borderColor: 'blue.300' }}
                              _focus={{ borderColor: 'blue.500', boxShadow: '0 0 0 3px rgba(66, 153, 225, 0.15)' }}
                            />
                          </InputGroup>
                        </FormControl>
                      </GridItem>
                    </Grid>

                    <FormControl>
                      <FormLabel fontSize="sm" fontWeight="bold" color={textColor}>Team Assignment</FormLabel>
                      <RadioGroup value={newPerson.team} onChange={(value) => setNewPerson({ ...newPerson, team: value })}>
                        <Grid templateColumns="repeat(3, 1fr)" gap={4}>
                          {['PS team', 'Implementation team', 'SD team'].map(team => (
                            <GridItem key={team}>
                              <Box
                                p={4}
                                borderRadius="xl"
                                border="2px solid"
                                borderColor={newPerson.team === team ? 'blue.500' : borderColor}
                                bg={newPerson.team === team ? teamBoxBg : cardBg}
                                transition="all 0.2s"
                                cursor="pointer"
                                _hover={{ borderColor: 'blue.400', transform: 'translateY(-2px)', shadow: 'md' }}
                              >
                                <Radio value={team} colorScheme="blue" size="lg">
                                  <Text fontSize="sm" fontWeight="semibold" color={textColor}>
                                    {team}
                                  </Text>
                                </Radio>
                              </Box>
                            </GridItem>
                          ))}
                        </Grid>
                      </RadioGroup>
                    </FormControl>

                    <Box p={5} bg={cardBg} borderRadius="xl" border="2px solid" borderColor={borderColor} _hover={{ borderColor: 'purple.300' }} transition="all 0.2s">
                      <FormControl display="flex" alignItems="center" justifyContent="space-between">
                        <HStack spacing={3}>
                          <Box p={2} bg="purple.500" borderRadius="lg" color="white">
                            <Icon viewBox="0 0 24 24" boxSize={5}>
                              <path fill="currentColor" d="M12,4A4,4 0 0,1 16,8A4,4 0 0,1 12,12A4,4 0 0,1 8,8A4,4 0 0,1 12,4M12,14C16.42,14 20,15.79 20,18V20H4V18C4,15.79 7.58,14 12,14Z" />
                            </Icon>
                          </Box>
                          <VStack align="start" spacing={0}>
                            <FormLabel mb="0" fontSize="md" fontWeight="bold" color={textColor}>Team Lead</FormLabel>
                            <Text fontSize="sm" color={mutedColor}>Mark this person as the team lead</Text>
                          </VStack>
                        </HStack>
                        <Switch
                          isChecked={newPerson.isLead === 1}
                          onChange={(e) => setNewPerson({ ...newPerson, isLead: e.target.checked ? 1 : 0 })}
                          colorScheme="purple"
                          size="lg"
                        />
                      </FormControl>
                    </Box>

                    <Flex gap={3} pt={2}>
                      <Button flex="1" variant="outline" onClick={handleToggleAddForm} borderRadius="xl" size="lg" borderWidth="2px" _hover={{ bg: hoverBg }}>
                        Cancel
                      </Button>
                      <Button
                        flex="1"
                        colorScheme="blue"
                        onClick={handleAddPerson}
                        isLoading={isSubmitting}
                        loadingText={editingPerson ? "Updating..." : "Adding..."}
                        borderRadius="xl"
                        size="lg"
                        shadow="md"
                        _hover={{ transform: 'translateY(-2px)', shadow: 'lg' }}
                        transition="all 0.2s"
                      >
                        {editingPerson ? 'Update Person' : 'Add Person'}
                      </Button>
                    </Flex>
                  </VStack>
                </Box>
              )}

              <Box>
                {loadingSupportPersons ? (
                  <Flex justify="center" align="center" py={20}>
                    <VStack spacing={4}>
                      <Spinner size="xl" color="blue.500" thickness="4px" />
                      <Text color={mutedColor} fontSize="lg">Loading support team...</Text>
                    </VStack>
                  </Flex>
                ) : supportPersons.length > 0 ? (
                  <Box>
                    <Flex justify="space-between" align="center" mb={4}>
                      <Text fontSize="lg" fontWeight="bold" color={textColor}>
                        Team Members ({supportPersons.length})
                      </Text>
                    </Flex>
                    <TableContainer borderRadius="xl" border="1px solid" borderColor={borderColor} bg={cardBg}>
                      <Table variant="simple" size="md">
                        <Thead bg={bg}>
                          <Tr>
                            <Th color={mutedColor} textTransform="uppercase" fontSize="xs" fontWeight="bold" letterSpacing="wider" py={4}>Name</Th>
                            <Th color={mutedColor} textTransform="uppercase" fontSize="xs" fontWeight="bold" letterSpacing="wider" py={4}>Email</Th>
                            <Th color={mutedColor} textTransform="uppercase" fontSize="xs" fontWeight="bold" letterSpacing="wider" py={4}>Role</Th>
                            <Th color={mutedColor} textTransform="uppercase" fontSize="xs" fontWeight="bold" letterSpacing="wider" py={4}>Team</Th>
                            {userProfile?.role === 'admin' && (
                              <Th color={mutedColor} textTransform="uppercase" fontSize="xs" fontWeight="bold" letterSpacing="wider" py={4}>Actions</Th>
                            )}
                          </Tr>
                        </Thead>
                        <Tbody>
                          {supportPersons.map((person, index) => (
                            <Tr key={person.id} _hover={{ bg: hoverBg }} transition="all 0.2s" borderBottom={index === supportPersons.length - 1 ? 'none' : '1px solid'} borderColor={borderColor}>
                              <Td fontWeight="bold" color={textColor} fontSize="md" py={5}>
                                <HStack spacing={3}>
                                  <Box w="10" h="10" borderRadius="full" bg="blue.500" color="white" display="flex" alignItems="center" justifyContent="center" fontWeight="bold" fontSize="sm">
                                    {person.name.charAt(0).toUpperCase()}
                                  </Box>
                                  <Text>{person.name}</Text>
                                </HStack>
                              </Td>
                              <Td>
                                <HStack spacing={2}>
                                  <EmailIcon boxSize={4} color="blue.500" />
                                  <Text fontSize="sm" color={textColor}>{person.email}</Text>
                                </HStack>
                              </Td>
                              <Td>
                                <Badge colorScheme={person.isLead === 1 ? 'purple' : 'gray'} variant="subtle" fontSize="xs" px={4} py={2} borderRadius="full" fontWeight="bold">
                                  {person.isLead === 1 ? 'Team Lead' : 'Member'}
                                </Badge>
                              </Td>
                              <Td>
                                <Badge colorScheme="blue" variant="outline" fontSize="xs" px={4} py={2} borderRadius="full" fontWeight="bold" borderWidth="2px">
                                  {person.team}
                                </Badge>
                              </Td>
                              {userProfile?.role === 'admin' && (
                                <Td>
                                  <HStack spacing={2}>
                                    <IconButton
                                      icon={<EditIcon />}
                                      size="sm"
                                      colorScheme="blue"
                                      variant="ghost"
                                      onClick={() => handleEditPerson(person)}
                                      aria-label="Edit person"
                                      _hover={{ bg: editButtonHoverBg }}
                                    />
                                    <IconButton
                                      icon={<DeleteIcon />}
                                      size="sm"
                                      colorScheme="red"
                                      variant="ghost"
                                      onClick={() => handleDeletePerson(person)}
                                      aria-label="Delete person"
                                      _hover={{ bg: deleteButtonHoverBg }}
                                    />
                                  </HStack>
                                </Td>
                              )}
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    </TableContainer>
                  </Box>
                ) : (
                  <Box py={20} px={10} bg={emptyStateBg} borderRadius="2xl" border="2px dashed" borderColor={borderColor}>
                    <VStack spacing={5}>
                      <Box p={6} bg={emptyStateIconBg} borderRadius="full" border="2px solid" borderColor={borderColor} shadow="md">
                        <EmailIcon boxSize={12} color={mutedColor} />
                      </Box>
                      <VStack spacing={2}>
                        <Heading size="md" color={textColor}>No Support Persons Found</Heading>
                        <Text color={mutedColor} fontSize="md" textAlign="center" maxW="400px">
                          There are currently no support persons assigned to this client. Add team members to get started.
                        </Text>
                      </VStack>
                      {userProfile?.role === 'admin' && !showAddForm && (
                        <Button
                          leftIcon={<AddIcon />}
                          colorScheme="blue"
                          size="lg"
                          onClick={handleToggleAddForm}
                          mt={3}
                          borderRadius="xl"
                          shadow="md"
                          px={8}
                          _hover={{ transform: 'translateY(-2px)', shadow: 'lg' }}
                          transition="all 0.2s"
                        >
                          Add First Person
                        </Button>
                      )}
                    </VStack>
                  </Box>
                )}
              </Box>
            </VStack>
          </ModalBody>
          
          <ModalFooter borderTop="1px solid" borderColor={borderColor} px={8} py={5} bg={modalFooterBg} borderBottomRadius="3xl">
            <Button onClick={onClose} variant="ghost" borderRadius="xl" colorScheme="gray" size="lg" px={8} _hover={{ bg: hoverBg }}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        isOpen={isDeleteOpen}
        leastDestructiveRef={cancelRef}
        onClose={onDeleteClose}
      >
        <AlertDialogOverlay backdropFilter="blur(8px)" bg="blackAlpha.700">
          <AlertDialogContent bg={cardBg} borderRadius="2xl" shadow="dark-lg" border="1px solid" borderColor={borderColor}>
            <AlertDialogHeader fontSize="lg" fontWeight="bold" color={textColor} borderBottom="1px solid" borderColor={borderColor}>
              <HStack spacing={3}>
                <Box p={2} bg="red.500" borderRadius="lg" color="white">
                  <DeleteIcon boxSize={5} />
                </Box>
                <Text>Delete Support Person</Text>
              </HStack>
            </AlertDialogHeader>

            <AlertDialogBody py={6}>
              <VStack spacing={4} align="start">
                <Text color={textColor}>
                  Are you sure you want to delete this support person? This action cannot be undone.
                </Text>
                {deletingPerson && (
                  <Box p={4} bg={emptyStateBg} borderRadius="xl" w="full" border="1px solid" borderColor={borderColor}>
                    <VStack align="start" spacing={2}>
                      <HStack spacing={3}>
                        <Box w="10" h="10" borderRadius="full" bg="red.500" color="white" display="flex" alignItems="center" justifyContent="center" fontWeight="bold" fontSize="sm">
                          {deletingPerson.name.charAt(0).toUpperCase()}
                        </Box>
                        <VStack align="start" spacing={0}>
                          <Text fontWeight="bold" color={textColor}>{deletingPerson.name}</Text>
                          <Text fontSize="sm" color={mutedColor}>{deletingPerson.email}</Text>
                        </VStack>
                      </HStack>
                      <HStack spacing={2}>
                        <Badge colorScheme={deletingPerson.isLead === 1 ? 'purple' : 'gray'} variant="subtle">
                          {deletingPerson.isLead === 1 ? 'Team Lead' : 'Member'}
                        </Badge>
                        <Badge colorScheme="blue" variant="outline">
                          {deletingPerson.team}
                        </Badge>
                      </HStack>
                    </VStack>
                  </Box>
                )}
              </VStack>
            </AlertDialogBody>

            <AlertDialogFooter borderTop="1px solid" borderColor={borderColor} gap={3}>
              <Button ref={cancelRef} onClick={onDeleteClose} borderRadius="xl" size="lg" variant="ghost" _hover={{ bg: hoverBg }}>
                Cancel
              </Button>
              <Button colorScheme="red" onClick={confirmDeletePerson} borderRadius="xl" size="lg" shadow="md" _hover={{ transform: 'translateY(-2px)', shadow: 'lg' }} transition="all 0.2s">
                Delete Person
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
}

export default ClientProfilePage;